import type { KnowledgeAnswerView, KnowledgeCitation, KnowledgeResourceLink } from '@shared/types';
import { dateLabel } from '../lib/format';
import { Markdown, Tag } from './ui';

export const PERMIT_NOTE =
  'HOA approval is not a city permit, and a city permit does not give HOA approval. You may need both. State law can limit some HOA restrictions.';

/** "HOA Guideline", "Official City Resource", "Unverified source" or "Link only". */
export function SourceBadge({
  section,
  official,
  linkOnly = false,
  authorityName,
}: {
  section: 'hoa' | 'municipal';
  official: boolean;
  linkOnly?: boolean;
  authorityName?: string;
}) {
  if (linkOnly) return <Tag tone="pend">Link only</Tag>;
  if (section === 'hoa') return <Tag tone="info">HOA Guideline</Tag>;
  if (!official) return <Tag tone="warn">Unverified source</Tag>;
  return <Tag tone="ok">Official {authorityName?.toLowerCase().includes('county') ? 'County' : 'City'} Resource</Tag>;
}

export function Freshness({ checkedAt, stale }: { checkedAt: string | null; stale: boolean }) {
  if (!checkedAt) return null;
  return (
    <span className={stale ? 'kc-stale' : 'mut'}>
      {stale ? 'May be out of date · ' : ''}Last checked {dateLabel(checkedAt.slice(0, 10))}
    </span>
  );
}

function isExternal(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

export function CitationCard({ citation, as: Tag_ = 'li' }: { citation: KnowledgeCitation; as?: 'li' | 'div' }) {
  return (
    <Tag_ className="kc-cite">
      <div className="kc-cite-head">
        <strong>{citation.title}</strong>
        <SourceBadge section={citation.section} official={citation.official} authorityName={citation.authorityName} />
      </div>
      {citation.location && <div className="mut">{citation.location}</div>}
      <blockquote>“{citation.excerpt}”</blockquote>
      <div className="kc-cite-foot">
        <Freshness checkedAt={citation.checkedAt} stale={citation.stale} />
        <a href={citation.url} target={isExternal(citation.url) ? '_blank' : undefined} rel="noopener noreferrer">
          {citation.section === 'hoa' ? 'Open document' : 'Open official source'}
          <span className="sr-only"> for {citation.title}</span>
        </a>
      </div>
    </Tag_>
  );
}

function SectionBlock({
  heading,
  section,
}: {
  heading: string;
  section: KnowledgeAnswerView['hoa'];
}) {
  return (
    <section className="kc-section" aria-label={heading}>
      <div className="kc-section-head">
        <h4>{heading}</h4>
        {section.found ? <Tag tone="ok">Found</Tag> : <Tag tone="pend">Not found</Tag>}
      </div>
      <Markdown source={section.summary} />
      {section.citations.length > 0 && (
        <details className="kc-drawer">
          <summary>
            {section.citations.length} citation{section.citations.length === 1 ? '' : 's'}
          </summary>
          <ul className="kc-cites">
            {section.citations.map((citation, index) => (
              <CitationCard key={`${citation.sourceId}-${index}`} citation={citation} />
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

export function OfficialLinks({ links }: { links: KnowledgeResourceLink[] }) {
  if (links.length === 0) return null;
  return (
    <div className="kc-links">
      {links.map((link) => (
        <a key={link.id} className="btn s tiny" href={link.url} target="_blank" rel="noopener noreferrer">
          {link.category === 'property_lookup' ? 'Open official property lookup' : `Open ${link.title}`}
        </a>
      ))}
    </div>
  );
}

export function KnowledgeAnswerBlock({
  answer,
  onFollowUp,
  onStartRequest,
}: {
  answer: KnowledgeAnswerView;
  onFollowUp?: (question: string) => void;
  /** Shown for homeowners when the question is about a home project. */
  onStartRequest?: () => void;
}) {
  return (
    <div className="kc-answer">
      <SectionBlock heading="HOA rules and approval" section={answer.hoa} />
      <SectionBlock heading="City / county rules and permits" section={answer.municipal} />
      {answer.unresolved.length > 0 && (
        <section className="kc-section kc-unresolved" aria-label="Still to verify">
          <h4>Still to verify</h4>
          <ul>
            {answer.unresolved.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      )}
      <p className="mut kc-note">{PERMIT_NOTE}</p>
      <OfficialLinks links={answer.links} />
      {(onStartRequest || (onFollowUp && answer.followUps.length > 0)) && (
        <div className="chip-row kc-followups">
          {onStartRequest && (
            <button type="button" className="btn tiny" onClick={onStartRequest}>
              Start Architectural Request
            </button>
          )}
          {onFollowUp &&
            answer.followUps.map((item) => (
              <button key={item} type="button" className="chip" onClick={() => onFollowUp(item)}>
                {item}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
